<template>
  <div
    class="crafting-wrapper"
    :class="{
      'max-height':
        isConfirmationOpen ||
        ($device.isMobile && isModalOpen) ||
        ($device.isMobile && isModalQueueTimeOpen) ||
        ($device.isMobile && isMaxHeight)
    }"
  >
    <CraftingConfirmScreen
      v-if="isConfirmationOpen && tokenMetadataComputed"
      :recipe="currentRecipe"
      :token-metadata="tokenMetadataComputed"
      :crafting-multiplier="craftingMultiplier"
    />
    <CraftingDescriptionPopup
      v-if="$device.isMobile && isModalOpen"
      :title="$t('craftingDescriptionTitle')"
      @toggle="toggleModal"
    >
      <CraftingModalInfoTooltip />
    </CraftingDescriptionPopup>

    <div class="crafting-container">
      <div class="crafting-header">
        <div class="crafting-header-icons" @click="toggleModal">
          <el-tooltip :disabled="$device.isMobile" placement="bottom-end" popper-class="popper-default">
            <template #content>
              <CraftingModalInfoTooltip />
            </template>
            <div class="crafting-header-icons-info" />
          </el-tooltip>
        </div>
        <div class="crafting-text">
          <h2 class="crafting-title">{{ $t(props.selectedBuilding.buildingKey) }}</h2>
          <p class="crafting-description">{{ $t(props.selectedBuilding.descriptionKey || '') }}</p>
        </div>
      </div>
      <div v-if="claimItems.length" class="claimable-tokens">
        <CraftingReadyForClaimMaterials
          :token-metadata-computed="tokenMetadataComputed"
          :disabled="isClaiming"
          :is-claim-button-loading="isClaimButtonLoading"
          :selected-building="props.selectedBuilding"
          @claim-load-state-changed="(isLoading) => (isClaiming = isLoading)"
        />
      </div>

      <div class="crafting-content">
        <div
          :style="$device.isDesktop && { maxHeight: `${craftingTableElComputedHeight}px` }"
          class="crafting-available-craft"
        >
          <h2 class="crafting-available-craft-title">{{ $t('craftingRecipesTitle') }}</h2>
          <div class="crafting-available-craft-title-mobile">
            <img
              class="crafting-available-craft-title-mobile-arrow"
              :class="{ disabled: (scrollLeftRef || 0) <= 0 }"
              src="/img/icons/arrowRightThin.svg"
              alt="arrowLeft"
              @click="handleScrollRecipes(false)"
            />
            <h3>{{ $t('craftingChooseRecipe') }}</h3>
            <img
              class="crafting-available-craft-title-mobile-arrow"
              :class="{
                disabled:
                  scrollLeftRef >= (craftingListDOMRef?.scrollWidth || 0) - (craftingListDOMRef?.clientWidth || 0)
              }"
              src="/img/icons/arrowRightThin.svg"
              alt="arrowRight"
              @click="handleScrollRecipes(true)"
            />
          </div>
          <div
            ref="craftingListDOMRef"
            class="crafting-list"
            :class="{
              'crafting-list-max-height-auto': isMaxHeightAuto
            }"
            @scroll="(e) => (scrollLeftRef = (e?.target as HTMLElement)?.scrollLeft)"
          >
            <div
              v-for="(recipe, index) of recipesFilteredList"
              :key="index"
              class="crafting-item"
              :class="[recipe?.recipeId === selectedRecipeId && 'selectedItem']"
              @click="handleSelectItemToCraft(recipe?.recipeId, index)"
            >
              <figure class="crafting-item-figure">
                <img :src="getIcon(Number(recipe.recipeId), recipe.products[0].tokenAddress)" alt="" />
              </figure>
              <div class="crafting-item-content">
                <h4
                  class="crafting-item-name"
                  :class="{ 'ru-locale-name': $i18n.locale === 'ru', 'de-locale-name': $i18n.locale === 'de' }"
                >
                  {{ getCleanRecipeName(recipe, recipeIdPrefix) }}
                </h4>
                <template v-if="getRecipeSpeedLevel(recipe, recipeIdPrefix) === 1">
                  <img src="/img/crafting/speedRecipeIcon.svg" alt="fast" />
                </template>
                <template v-else-if="getRecipeSpeedLevel(recipe, recipeIdPrefix) === 2">
                  <div style="display: flex">
                    <img src="/img/crafting/speedRecipeIcon.svg" alt="fast" />
                    <img src="/img/crafting/speedRecipeIcon.svg" alt="fast" />
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div ref="craftingTableElRef" class="crafting-table">
          <CraftingProduceItem
            v-if="tokenMetadataComputed && currentRecipe"
            :token-metadata="tokenMetadataComputed"
            :building-key="props.selectedBuilding.buildingKey"
            :recipe="currentRecipe"
            :parent-element-ref="craftingTableElRef"
            :is-claim-button-loading="isClaimButtonLoading"
            @claim-load-state-changed="(isLoading) => (isClaimButtonLoading = isLoading)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Crafting__factory } from 'public/abi/evm/types';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { ElNotification } from 'element-plus';
import { BigNumber } from 'bignumber.js';
import { type TNullable } from '~/types/common';
import type {
  BuildingProps,
  UserActivityAvailableForClaim,
  ActiveUsersModalProps,
  RecipeProps,
  RecipeProduct,
  RunningRecipeProps
} from '~/types/crafting';
import { apiUrls, Tokens } from '~/utils/constants';
import { Formatters } from '~/utils/formatters/formatters';

const props = defineProps<{
  selectedBuilding: BuildingProps;
}>();

const emit = defineEmits<{
  (e: 'closed'): void;
}>();

const closeModal = () => {
  emit('closed');
};

provide('closeModal', closeModal);

const { t } = useI18n();
const route = useRoute();
const { getProvider } = useProvider();
const { $getIconToken } = useNuxtApp();
const store = useMainStore();
const { address } = useWeb3ModalAccount();
const { sendContractMethod } = useSendContractMethod();
const { apiUrl, blockchain } = useEnvs();
const claimItems = computed(() => {
  if (!props.selectedBuilding?.buildingHash) {
    return [];
  }
  return store.getClaimItemsForBuilding(props.selectedBuilding.buildingHash);
});

const config = useRuntimeConfig();
const { isMobile } = useDevice();
const [isModalOpen, toggleModal] = useToggle();
const [isModalQueueTimeOpen, toggleModalQueueTime] = useToggle();
const isMaxHeight = ref(false);
const isMaxHeightAuto = ref(false);
const selectedRecipeId = ref<TNullable<string>>();
const computedBuildingHash = computed(() => props.selectedBuilding.buildingHash);

const { data: userRecipesInQueueForCurrentBuilding } = await useFetch<RunningRecipeProps[]>(apiUrls.crafting.inQueue, {
  baseURL: apiUrl,
  query: {
    buildingHash: computedBuildingHash
  },
  watch: [address],
  transform: (data) => {
    return data.filter((recipe) => recipe.userAddress === address.value);
  }
});

const userActivityRef = reactive<UserActivityAvailableForClaim[][]>([]);
const isConfirmationOpen = ref<boolean>(false);
const confirmationNumberRef = ref<number>(0);
const isItemCrafting = ref(false);
const craftingMultiplier = ref<number>(1);
const craftingTime = ref<string>('');
const craftingListDOMRef = ref<HTMLElement | null>(null);
const craftingTableElRef = ref<HTMLElement | null>(null);
const craftingTableElComputedHeight = ref<number>(craftingTableElRef.value?.clientHeight || 420);

const scrollLeftRef = ref<number>(0);
const isClaiming = ref(false);

// Shared loading state for claim buttons across crafting components
const isClaimButtonLoading = ref<boolean>(false);

useResizeObserver(craftingTableElRef, (entries) => {
  const entry = entries[0];
  const { height } = entry.contentRect;

  if (document.documentElement.clientWidth <= 900) {
    return;
  }

  craftingTableElComputedHeight.value = height;
});

const toggleMaxHeightCraftingModal = () => {
  isMaxHeight.value = !isMaxHeight.value;
};

provide('isClaiming', isClaiming);
provide('isClaimButtonLoading', isClaimButtonLoading);
provide('handleProduceItem', handleProduceItem);
provide('handleToggleConfirmationModal', handleToggleConfirmationModal);
provide('setProductMultiplier', setProductMultiplier);
provide('setCraftingTime', setCraftingTime);
provide('craftingMultiplier', craftingMultiplier);
provide('craftingTime', craftingTime);
provide('confirmationNumberRef', confirmationNumberRef);
provide('isItemCrafting', isItemCrafting);
provide('userActivity', userActivityRef);
provide('toggleModalQueueTime', toggleModalQueueTime);
provide('toggleMaxHeightCraftingModal', toggleMaxHeightCraftingModal);

const activeUsersModal = ref<ActiveUsersModalProps>({
  craftingViewersCount: 0
});
provide('activeUsersModal', activeUsersModal);

const { data: recipes } = useNuxtData<RecipeProps[]>(
  `buildingRecipes${isMobile && route.name !== 'districts' ? props.selectedBuilding.buildingHash : ''}`
);

const recipesFilteredList = computed(() =>
  recipes.value?.filter(
    (recipe) =>
      recipe.isActive ||
      userRecipesInQueueForCurrentBuilding.value?.some((r) => BigNumber(r.recipeId).eq(recipe.recipeId))
  )
);
watch(
  [recipes, address],
  (newValues) => {
    const [newRecipes] = newValues;

    if (newRecipes) {
      selectedRecipeId.value = recipesFilteredList.value?.[0]?.recipeId;
    }
  },
  { immediate: true }
);

const currentRecipe = computed(() =>
  recipesFilteredList?.value?.find((recipe) => recipe?.recipeId === selectedRecipeId.value)
);
provide('currentRecipe', currentRecipe);

const recipeIdPrefix = computed(() => {
  const env = config?.public.env;
  return env === 'prod' ? 'prodRecipeId' : env === 'demo' ? 'demoRecipeId' : 'testnetRecipeId';
});

const handleScrollRecipes = (isNext: boolean) => {
  craftingListDOMRef.value?.scroll({
    left: isNext ? scrollLeftRef.value + 150 : scrollLeftRef.value - 150,
    behavior: 'smooth'
  });
};

const tokenMetadataComputed = computed(() => {
  if (!currentRecipe.value?.products.length) return null;

  if (currentRecipe.value) {
    return tokenMetaData(blockchain.contracts, currentRecipe.value?.products[0].tokenAddress);
  }
  return null;
});

const tokenUniqueness = computed(() => {
  if (!currentRecipe.value) return '';

  const product = currentRecipe.value?.products[0];
  const value = getNftValueByAddress(blockchain.contracts, product);

  return value;
});

const isNft = computed(() => {
  if (!currentRecipe.value) return false;

  return (
    currentRecipe.value &&
    currentRecipe.value.products.every(
      (product: RecipeProduct) => product.nftValueMin !== null && product.nftValueMax !== null
    )
  );
});

function setProductMultiplier(multiplier: number) {
  craftingMultiplier.value = multiplier;
}

function setCraftingTime(formattedTime: string) {
  craftingTime.value = formattedTime;
}

function handleSelectItemToCraft(recipeId: string, index: number) {
  const currentRecipeImage = getTokenImg(index);
  setTokenImgToStorage(currentRecipeImage);
  selectedRecipeId.value = recipeId;
}

function getTokenImg(index: number): string {
  if (!recipesFilteredList.value) return '';

  const img = $getIconToken(
    recipesFilteredList.value[index].products[0],
    BigNumber(recipesFilteredList.value[index].recipeId || 0).toNumber()
  );

  return img;
}

function setTokenImgToStorage(img: string): void {
  store.setCraftingTokenImg(img);
}

function handleToggleConfirmationModal() {
  isConfirmationOpen.value = !isConfirmationOpen.value;
}

async function handleProduceItem(recipe: RecipeProps) {
  isItemCrafting.value = true;

  try {
    const produceTx = await sendContractMethod(
      {
        contract: Crafting__factory.connect(blockchain.contracts.crafting, getProvider()),
        methodName: 'useRecipe(bytes32,uint256,uint256)',
        args: [props.selectedBuilding?.buildingHash, Number(selectedRecipeId.value ?? 0), craftingMultiplier.value]
      },
      () => {
        confirmationNumberRef.value = confirmationNumberRef.value + 1;
      }
    );

    if (!produceTx?.hash) return;

    let description = `${tokenMetadataComputed.value?.methodName} ${tokenMetadataComputed.value?.name.contract !== Tokens.woodenHuts ? Formatters.text.firstLetterUppercase(tokenUniqueness.value) : ''}`;

    if (craftingMultiplier.value > 1) {
      description = `${craftingMultiplier.value} ${description}`;
    }

    if (!isNft.value) {
      const values = recipe?.products.map((product) => {
        if (new BigNumber(product.quantityMin).eq(product.quantityMax)) {
          return BigNumber(product.quantityMin).multipliedBy(craftingMultiplier.value).decimalPlaces(1).toNumber();
        }

        const minValue = new BigNumber(product.quantityMin).multipliedBy(craftingMultiplier.value).decimalPlaces(1);
        const maxValue = new BigNumber(product.quantityMax).multipliedBy(craftingMultiplier.value).decimalPlaces(1);

        return `${minValue}-${maxValue}`;
      });

      description = recipe?.products
        .map(
          (product, index) =>
            `${(values && values[index]) || ''} ${t(tokenMetaData(blockchain.contracts, product.tokenAddress || '').methodName)}`
        )
        .join(', ')
        .trim() as string;
    }

    saveHashToLocalStorage(`${t('craftingCraftingTab')} (${description})`, produceTx.hash);

    store.updateVersion();

    await produceTx?.wait();

    modifyHistoryHashStatus(produceTx.hash, 'Done');
    confirmationNumberRef.value = 0;
    store.updateVersion();

    const params = {
      recipeId: selectedRecipeId.value,
      transactionHash: produceTx.hash,
      userAddress: address.value,
      landBuildingHash: props.selectedBuilding?.buildingHash
    };

    await fetch(apiUrl + `/crafting/activity`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params)
    });
  } catch (error) {
    console.error(error);
    ElNotification.error({
      title: t('craftingErrorWhileCrafting'),
      message: ''
    });
  } finally {
    isConfirmationOpen.value = false;
    isItemCrafting.value = false;
  }
}
</script>

<style lang="scss">
.el-dialog.crafting.produce {
  max-width: 900px;
  min-height: 650px;

  .el-dialog {
    &__body {
      padding-top: 0 !important;
      padding-bottom: 0 !important;
    }
  }
}

.crafting-wrapper {
  font-family: Grenze, sans-serif;
  min-height: fit-content;

  @media screen and (max-width: 479px) {
    min-height: auto;
  }
}

.claimable-tokens {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 15px 17px;
  border-radius: 12px;
  border: 2px solid #1e353e;
  background: linear-gradient(180deg, #60ff7c 0%, #04202b 100%);
  max-width: 760px;
  margin: 0 auto;

  .ready-for-claim-item {
    margin-right: 10px;
  }

  @media screen and (max-width: 767px) {
    margin-top: 20px;
  }
}

.claimable-blocks-wrapper {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.ready-for-claim {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 15px;
  width: 100%;

  @media screen and (max-width: 767px) {
    max-height: 100%;
    justify-content: center;
    flex-wrap: wrap;
  }

  @media screen and (max-width: 900px) {
    width: max-content;
  }

  .checkbox-group {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    flex: 1 1 auto;

    @media screen and (max-width: 767px) {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
      justify-items: center;
    }

    .ready-for-claim-item {
      display: flex;
      align-items: center;

      span {
        margin-bottom: 0;
        margin-left: 8px;
        font-weight: 800;
        font-size: 24px;
        color: #fff;
        text-wrap: nowrap;
      }

      .crafting-item-figure {
        width: 44px;
        height: 44px;
        min-height: 44px;
        min-width: 44px;
        display: flex;
        align-items: center;
        margin: 0;
        font-family: Eczar, sans-serif;

        img {
          width: 100%;
        }

        img.nftBackground {
          border-radius: 12px;
          background-color: #041a27;
          border: solid 1px #1e353e;
        }
      }
    }
  }

  button {
    height: 40px;
    vertical-align: middle;
    padding-top: 7px;
    padding-bottom: 7px;
    font-size: clamp(14px, 2vw, 16px);

    svg {
      path {
        transition: all 0.3s;
      }
    }

    &:hover {
      svg {
        path {
          fill: #fff !important;
        }
      }
    }
  }

  button:nth-child(2) {
    margin-left: 5px;
  }

  &-content {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
  }

  @media screen and (max-width: 900px) {
    width: 100%;
  }

  @media screen and (max-width: 767px) {
    max-width: 100%;
  }
}

.crafting-item-figure {
  max-width: 50px;
  max-height: 50px;
  display: flex;
  align-items: center;
  margin: 0;
  font-family: Eczar, sans-serif;

  p {
    margin-bottom: 0;
    margin-left: 5px;
    font-weight: 800;
    font-size: 24px;
    color: #fff;
    text-wrap: nowrap;
  }

  img {
    width: 100%;
  }
}

.crafting {
  &-header {
    position: relative;

    @media screen and (max-width: 767px) {
      position: static;
    }
  }

  &-header-icons {
    position: absolute;
    left: 0;
    top: 0;
    display: flex;

    @media screen and (max-width: 900px) {
      top: 23px;
      left: 20px;
    }
  }

  &-header-icons-stats,
  &-header-icons-info {
    cursor: pointer;

    width: 20px;
    height: 20px;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: 50%;

    &:hover {
      opacity: 0.6;
    }
  }

  &-header-icons-stats {
    background-image: url('~/public/img/icons/stats.svg');
  }

  &-header-icons-info {
    background-image: url('~/public/img/icons/info.svg');
  }

  &-mobile-header {
    display: none;

    h2 {
      font-size: 40px;
      color: #fff;
      text-align: center;
    }

    @media screen and (max-width: 900px) {
      display: block;
    }
  }

  &-content {
    display: flex;
    margin-top: 16px;
    align-items: flex-start;
    min-height: 420px;
    height: min-content;

    @media screen and (max-width: 900px) {
      flex-direction: column;
    }
  }

  &-container {
    display: flex;
    flex-direction: column;
    position: relative;

    & > img {
      max-width: 215px;
    }

    @media screen and (max-width: 900px) {
      flex-direction: column;
    }

    @media screen and (max-width: 767px) {
      position: static;
    }
  }

  &-available-craft {
    position: relative;

    width: 100%;
    max-width: 195px;

    padding: 10px 7px;

    background: #0d242f;
    border: 1px solid #1e353e;
    border-radius: 15px;
    display: flex;
    flex-direction: column;

    @media screen and (max-width: 900px) {
      max-width: 100%;
    }
  }

  &-available-craft-title {
    color: #fff;
    text-align: center;
    margin-bottom: 10px;
    margin-top: 0;
    display: block;

    @media screen and (max-width: 900px) {
      display: none;
    }
  }

  &-available-craft-title-mobile {
    display: none;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;

    color: #90989d;
    font-weight: 500;
    font-size: 18px;

    h3 {
      margin: 0 10px;
    }

    @media screen and (max-width: 900px) {
      display: flex;
    }
  }

  &-available-craft-title-mobile-arrow {
    &:nth-child(1) {
      transform: rotate(180deg);
    }

    &.disabled {
      opacity: 0.3;
    }
  }

  &-list {
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
    scrollbar-width: none;
    transition: height 0.5s ease;

    @media screen and (max-width: 900px) {
      overflow-y: hidden;
      display: flex;
      overflow-x: auto;
      max-width: 100%;
    }
  }

  // .crafting-list-max-height-auto {
  //   max-height: 100%;

  //   @media screen and (max-width: 900px) {
  //     max-height: unset;
  //   }
  // }

  &-item {
    display: flex;
    align-items: center;
    padding: 8px 7px;
    min-height: 65px;

    @media screen and (max-width: 900px) {
      flex: 0 0 160px;
    }

    background: linear-gradient(190.74deg, #425b6a 7.97%, #0e1b25 95.05%);

    border: 1px solid #1e353e;
    border-radius: 12px;

    transition: all 0.3s ease;
    cursor: pointer;

    &:not(:first-child) {
      margin-top: 9px;

      @media screen and (max-width: 900px) {
        margin-top: 0;
        margin-left: 10px;
      }
    }

    &:hover {
      background: linear-gradient(180deg, #0083ad 0%, #00354f 100%), #081b25;
    }

    &-figure {
      width: 44px;
      height: 44px;
      min-height: 44px;
      min-width: 44px;
      display: flex;
      align-items: center;
      margin: 0;
      font-family: Eczar, sans-serif;

      img {
        width: 100%;
        border-radius: 8px;
      }

      p {
        margin-bottom: 0;
        margin-left: 5px;
        font-weight: 800;
        font-size: 24px;
        color: #fff;
        text-wrap: nowrap;
      }
    }

    &-name {
      color: #fff;
      font-family: Eczar, sans-serif;
      font-weight: 600;
      font-size: 16px;
      margin-bottom: 0px;
      text-align: left;
      word-break: break-word;
      overflow-wrap: break-word;
      hyphens: auto;

      @media screen and (max-width: 900px) {
        margin-left: auto;
        margin-right: auto;
      }
    }

    .ru-locale-name {
      font-weight: 500;
      font-size: 14px;
    }

    .de-locale-name {
      font-weight: 500;
      font-size: 14px;
    }
  }

  &-item.selectedItem {
    background: linear-gradient(180deg, #0083ad 0%, #00354f 100%), #081b25;
  }

  &-item-figure {
    width: 44px;
    height: 44px;
    min-height: 44px;
    min-width: 44px;
    display: flex;
    align-items: center;
    margin: 0;
    font-family: Eczar, sans-serif;

    img {
      width: 100%;
      border-radius: 8px;
    }

    p {
      margin-bottom: 0;
      margin-left: 5px;
      font-weight: 800;
      font-size: 24px;
      color: #fff;
      text-wrap: nowrap;
    }
  }

  &-item-content {
    margin-left: 10px;

    img {
      width: 12px;
      height: 16px;
    }
  }

  &-item-name {
    color: #fff;
    font-family: Eczar, sans-serif;
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 2px;
    text-align: left;
    word-break: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    word-break: keep-all;

    @media screen and (max-width: 900px) {
      margin-left: auto;
      margin-right: auto;
    }
  }

  &-table {
    width: 100%;
    max-width: calc(100% - 180px);
    padding-left: 10px;

    @media screen and (max-width: 900px) {
      max-width: unset;
      padding-left: 0;
      padding-top: 10px;
    }
  }

  &-text {
    text-align: center;
    margin-bottom: 0;

    @media screen and (max-width: 900px) {
      display: none;
    }
  }

  &-title {
    color: #fff;

    font-weight: 700;
    font-size: 32px;

    margin: 0;
    margin-bottom: 15px;
  }

  &-description {
    color: #90989d;
    text-align: center;
    font-size: 20px;
    margin-bottom: 0;
    margin: auto;
  }

  &-materials {
    margin: 0;
    padding: 0;

    list-style: none;

    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: stretch;
    gap: 10px;
  }

  &-recipe {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 16px;

    @media screen and (max-width: 900px) {
      flex-direction: column;
    }
  }

  &-arrow {
    font-size: 32px;
    color: #88e1ff;
    margin: 0 10px;

    @media screen and (max-width: 900px) {
      transform: rotate(90deg);
      margin-top: 10px;
      margin-bottom: 10px;
    }
  }

  &-crafted {
    padding: 0px 11px;
    border: 1px solid #1e353e;
    border-radius: 12px;

    background: #041a27;
    box-shadow: 2px 4px 5px 0px #00000040;
    background: linear-gradient(180deg, #0083ad 0%, #00354f 100%);
  }

  .el-input-number {
    width: 130px;
    flex-shrink: 0;

    @media screen and (max-width: 500px) {
      width: 160px;
      height: 48px;

      &__decrease,
      &__increase {
        width: 32px;
      }
    }

    @media screen and (max-width: 376px) {
      width: auto;
      min-width: 120px;
    }

    .el-input {
      background: radial-gradient(59.02% 71.08% at 84.01% 85.82%, #040f14 0%, #081b25 100%) !important;

      @media screen and (max-width: 500px) {
        font-size: 18px;
      }

      font-size: 18px;

      &__inner {
        color: #cfd3dc;
        --el-input-inner-height: 32px;

        @media screen and (max-width: 500px) {
          --el-input-inner-height: 32px;
        }
      }

      &__wrapper {
        background: radial-gradient(59.02% 71.08% at 84.01% 85.82%, #040f14 0%, #081b25 100%) !important;

        @media screen and (max-width: 500px) {
          padding: unset;
        }

        input {
          padding: 0;
        }
      }
    }
  }
}

.max-height {
  max-height: 90vh;
  overflow: hidden;

  @media (max-width: 479px) {
    max-height: 70vh;
    overflow-y: auto;
  }
}
</style>
